import React, { useState, useEffect } from "react";
import AddIcon from '@mui/icons-material/Add';
import { useFilterManager } from "../../../hooks/useFilterManager";
import { useMedicalClearance } from "../../../hooks/useMedicalClearance";
import { DynamicFilterBar } from "../../filters/DynamicFilterBar";
import { Button, SearchBar } from "../../../../common/APICommon";
import { ConfirmationModal } from "../../../../common/Modal";
import MedicalClearanceAssignmentModal from "./components/MedicalClearanceAssignmentModal";
import MedicalClearanceProcessorModal from "./components/MedicalClearanceProcessorModal";
import MedicalClearanceTable from "./components/MedicalClearanceTable";

const MedicalClearanceModule = ({ user, userAccess, showGeneralMessage }) => {
  const [isAssignOpen, setIsAssignOpen] = useState(false);
  const [isProcessOpen, setIsProcessOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmConfig, setConfirmConfig] = useState({
    title: "",
    message: "",
    type: "info",
    action: null
  });

  const { activeFilterState, onFilterChange, apiFilterPayload, applyFilters, resetFilters } =
    useFilterManager(['VESSEL', 'EMPLOYEE_TYPE']);

  const {
    loading,
    data,
    requirements,
    handleSearch,
    createAssignment,
    updateAssignment,
    releaseAssignment,
    deleteAssignment,
    fetchRequirements,
    getAssignmentDetails,
    saveBatchDetails,
    finalizeClearance,
    refresh
  } = useMedicalClearance({
    showGeneralMessage,
    apiFilterPayload,
    user,
    userAccess
  });

  useEffect(() => {
    fetchRequirements();
  }, [fetchRequirements]);

  const handleAssignSave = async (form) => {
    let success = false;
    if (selectedRow) {
      success = await updateAssignment(selectedRow.id, form);
    } else {
      success = await createAssignment(form);
    }

    if (success) {
      setIsAssignOpen(false);
      setSelectedRow(null);
    }
  };

  const handleDelete = (row) => {
    setConfirmConfig({
      title: "Delete Draft",
      message: `Are you sure you want to delete the medical clearance draft for ${row.employeeFullName}? This action cannot be undone.`,
      type: "danger",
      action: async () => {
        await deleteAssignment(row.id);
        setShowConfirm(false);
      }
    });
    setShowConfirm(true);
  };

  const handleRelease = (row) => {
    setConfirmConfig({
      title: "Release to Clinic",
      message: `Are you sure you want to release ${row.transactionNumber} to the Clinic? The assignment manager will no longer be able to edit this record.`,
      type: "warning",
      action: async () => {
        await releaseAssignment(row.id);
        setShowConfirm(false);
      }
    });
    setShowConfirm(true);
  };

  const handleEdit = (row) => {
    setSelectedRow(row);
    setIsAssignOpen(true);
  };

  const handleCreateNew = () => {
    setSelectedRow(null);
    setIsAssignOpen(true);
  };

  return (
    <div className="p-4 space-y-4">
      <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm" style={{ border: "1px solid #d7d7d7" }}>
        <DynamicFilterBar
          requestedFilters={['VESSEL', 'EMPLOYEE_TYPE']}
          activeFilterState={activeFilterState}
          onFilterChange={onFilterChange}
          showButtons={false}
        />
        <div className="flex gap-2 mt-4 pt-4 border-t">
          <Button name="GENERATE" className="bg-blue-600 text-white px-6 py-2 border-none rounded cursor-pointer font-bold" onClick={applyFilters} />
          <Button name="CLEAR" className="bg-gray-400 text-white px-6 py-2 border-none rounded cursor-pointer" onClick={resetFilters} />
        </div>
      </div>

      <div className="flex items-center justify-between border-b">
        <SearchBar handleFilter={handleSearch} />

        {userAccess?.isMedicalClearanceManager && (
          <Button
            name="CREATE ASSIGNMENT"
            icon={<AddIcon />}
            className="bg-customGreen hover:bg-customGreenHover text-white border-none px-4 py-2 rounded shadow cursor-pointer font-bold"
            onClick={handleCreateNew}
          />
        )}
      </div>

      <MedicalClearanceTable
        data={data}
        onAction={(row) => {
          setSelectedRow(row);
          setIsProcessOpen(true);
        }}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onRelease={handleRelease}
        userAccess={userAccess}
      />

      <MedicalClearanceAssignmentModal
        isOpen={isAssignOpen}
        onClose={() => { setIsAssignOpen(false); setSelectedRow(null); }}
        onSave={handleAssignSave}
        loading={loading}
        selectedData={selectedRow}
        availableReqs={requirements}
        getAssignmentDetails={getAssignmentDetails}
      />

      <MedicalClearanceProcessorModal
        isOpen={isProcessOpen}
        onClose={() => { setIsProcessOpen(false); setSelectedRow(null); }}
        selectedHeader={selectedRow}
        canProcess={userAccess?.isMedicalClearanceProcessor}
        user={user}
        showGeneralMessage={showGeneralMessage}
        getAssignmentDetails={getAssignmentDetails}
        saveBatchDetails={saveBatchDetails}
        finalizeClearance={finalizeClearance}
        isLoading={loading}
      />

      <ConfirmationModal
        visible={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={confirmConfig.action}
        title={confirmConfig.title}
        message={confirmConfig.message}
        type={confirmConfig.type}
        isLoading={loading}
      />
    </div>
  );
};

export default MedicalClearanceModule;